"use client";

import { useState, useEffect, useCallback, useId, useRef } from "react";
import { Button } from "@/components/ui/button";
import { useTranslations } from "@/lib/use-translations";

export interface CoachMarkStep {
  target: string; // data-coach-mark value
  title: string;
  body: string;
  placement?: "above" | "below";
}

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;
const TOOLTIP_GAP = 12;

function getTargetRect(target: string): TargetRect | null {
  const el = document.querySelector(`[data-coach-mark="${target}"]`);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

export function CoachMarks({
  steps,
  onComplete,
}: {
  steps: CoachMarkStep[];
  onComplete: () => void;
}) {
  const maskId = useId();
  const t = useTranslations();
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Filter to only steps whose targets exist in the DOM
  const [visibleSteps, setVisibleSteps] = useState<CoachMarkStep[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
    placement: "above" | "below";
    arrowLeft: number;
  } | null>(null);

  // Resolve visible steps on mount
  useEffect(() => {
    const available = steps.filter((step) => getTargetRect(step.target) !== null);
    setVisibleSteps(available);
    if (available.length === 0) {
      onComplete();
    }
  }, [steps, onComplete]);

  const currentStep = visibleSteps[currentIndex];

  // Update target rect on changes, scroll, resize
  const updateRect = useCallback(() => {
    if (!currentStep) return;
    const rect = getTargetRect(currentStep.target);
    setTargetRect(rect);
  }, [currentStep]);

  useEffect(() => {
    updateRect();

    const onResize = () => updateRect();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);

    // ResizeObserver for layout changes
    const el = currentStep
      ? document.querySelector(`[data-coach-mark="${currentStep.target}"]`)
      : null;
    let observer: ResizeObserver | null = null;
    if (el) {
      observer = new ResizeObserver(onResize);
      observer.observe(el);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
      observer?.disconnect();
    };
  }, [currentStep, updateRect]);

  // Compute tooltip position after targetRect and tooltip dimensions are known
  useEffect(() => {
    if (!targetRect || !tooltipRef.current) return;

    const tooltipHeight = tooltipRef.current.offsetHeight;
    const tooltipWidth = tooltipRef.current.offsetWidth;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Determine placement
    let placement = currentStep?.placement;
    if (!placement) {
      const spaceBelow = viewportHeight - (targetRect.top + targetRect.height + PADDING);
      const spaceAbove = targetRect.top - PADDING;
      placement = spaceBelow >= tooltipHeight + TOOLTIP_GAP ? "below" : spaceAbove >= tooltipHeight + TOOLTIP_GAP ? "above" : "below";
    }

    // Vertical position
    let top: number;
    if (placement === "below") {
      top = targetRect.top + targetRect.height + PADDING + TOOLTIP_GAP;
    } else {
      top = targetRect.top - PADDING - TOOLTIP_GAP - tooltipHeight;
    }

    // Horizontal position — center on target, clamp to viewport
    let left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
    left = Math.max(12, Math.min(left, viewportWidth - tooltipWidth - 12));

    // Clamp vertical to viewport
    top = Math.max(12, Math.min(top, viewportHeight - tooltipHeight - 12));

    // Arrow horizontal position — centered on target, clamped within tooltip
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const arrowLeft = Math.max(16, Math.min(targetCenterX - left, tooltipWidth - 16));

    setTooltipPosition({ top, left, placement, arrowLeft });
  }, [targetRect, currentStep?.placement, currentIndex]);

  // Escape key to dismiss
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onComplete();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onComplete]);

  const handleNext = useCallback(() => {
    if (currentIndex >= visibleSteps.length - 1) {
      onComplete();
    } else {
      setCurrentIndex((prev) => prev + 1);
      setTooltipPosition(null);
    }
  }, [currentIndex, visibleSteps.length, onComplete]);

  if (visibleSteps.length === 0 || !currentStep || !targetRect) {
    return null;
  }

  const isLastStep = currentIndex === visibleSteps.length - 1;

  // Spotlight cutout dimensions (with padding)
  const cutout = {
    x: targetRect.left - PADDING,
    y: targetRect.top - PADDING,
    width: targetRect.width + PADDING * 2,
    height: targetRect.height + PADDING * 2,
    rx: 12,
  };

  return (
    <div className="fixed inset-0 z-[60]" aria-modal="true" role="dialog">
      {/* SVG overlay with mask cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: "none" }}>
        <defs>
          <mask id={maskId}>
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={cutout.x}
              y={cutout.y}
              width={cutout.width}
              height={cutout.height}
              rx={cutout.rx}
              fill="black"
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="black"
          opacity="0.6"
          mask={`url(#${CSS.escape(maskId)})`}
          style={{ pointerEvents: "auto" }}
          onClick={onComplete}
        />
      </svg>

      {/* Spotlight ring (animated pulse) */}
      <div
        className="absolute rounded-xl animate-coach-spotlight pointer-events-none"
        style={{
          top: cutout.y,
          left: cutout.x,
          width: cutout.width,
          height: cutout.height,
        }}
      />

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="absolute z-[61] w-72 max-w-[calc(100vw-24px)] bg-card border border-border rounded-xl shadow-lg p-4"
        style={{
          top: tooltipPosition?.top ?? -9999,
          left: tooltipPosition?.left ?? -9999,
          visibility: tooltipPosition ? "visible" : "hidden",
        }}
      >
        {/* Arrow pointer */}
        {tooltipPosition && (
          <div
            className="absolute"
            style={{
              left: tooltipPosition.arrowLeft,
              ...(tooltipPosition.placement === "below"
                ? { top: -8, borderLeft: "8px solid transparent", borderRight: "8px solid transparent", borderBottom: "8px solid hsl(var(--border))" }
                : { bottom: -8, borderLeft: "8px solid transparent", borderRight: "8px solid transparent", borderTop: "8px solid hsl(var(--border))" }),
              transform: "translateX(-8px)",
              width: 0,
              height: 0,
            }}
          >
            {/* Inner arrow for bg fill */}
            <div
              className="absolute"
              style={{
                ...(tooltipPosition.placement === "below"
                  ? { top: 1, left: -7, borderLeft: "7px solid transparent", borderRight: "7px solid transparent", borderBottom: "7px solid hsl(var(--card))" }
                  : { bottom: 1, left: -7, borderLeft: "7px solid transparent", borderRight: "7px solid transparent", borderTop: "7px solid hsl(var(--card))" }),
                width: 0,
                height: 0,
              }}
            />
          </div>
        )}
        <h3 className="text-sm font-semibold text-foreground mb-1">
          {currentStep.title}
        </h3>
        <p className="text-xs text-muted-foreground mb-3">
          {currentStep.body}
        </p>

        {/* Step dots + buttons */}
        <div className="flex items-center justify-between">
          {/* Step dots */}
          <div className="flex gap-1.5">
            {visibleSteps.map((_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === currentIndex
                    ? "bg-primary"
                    : i < currentIndex
                      ? "bg-primary/40"
                      : "bg-muted-foreground/30"
                }`}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex gap-2">
            {!isLastStep && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onComplete}
                className="h-7 px-2 text-xs text-muted-foreground"
              >
                {t.coachMarks.skip}
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleNext}
              className="h-7 px-3 text-xs"
            >
              {isLastStep ? t.coachMarks.done : t.coachMarks.next}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
